import { useMemo } from 'react'
import useSWR from 'swr'
import { MAX_WALLETS, ProfilesResponse, WalletProfile } from './logic'

const EMPTY: Record<string, WalletProfile> = {}

async function fetchChunk(wallets: string[]): Promise<Record<string, WalletProfile>> {
  const r = await fetch(`/api/competition-v3/profiles?wallets=${wallets.join(',')}`)
  if (!r.ok) throw new Error(String(r.status))
  const j = (await r.json()) as ProfilesResponse
  return j?.profiles ?? EMPTY
}

/**
 * The CADINUswap profiles of every wallet on a board, from ONE request (two when there are more than 100 wallets). The board never waits for this:
 * until it answers (or when it fails) the hook returns nothing and every row shows its address. The wallet set, not the order, is the cache key, so the
 * board's 5 s refresh does not ask again; a profile is looked at again every 5 minutes, and the server's own cache is 60 s for the on/off state.
 */
export function useProfiles(wallets: string[]): Record<string, WalletProfile> {
  const joined = wallets.join(',')
  const key = useMemo(() => {
    const list = Array.from(new Set(joined ? joined.split(',').map((w) => w.toLowerCase()) : [])).sort()
    return list.length ? `profiles:${list.join(',')}` : null
  }, [joined])
  const { data } = useSWR<Record<string, WalletProfile>>(
    key,
    async (k: string) => {
      const list = k.slice('profiles:'.length).split(',')
      const parts: string[][] = []
      for (let i = 0; i < list.length; i += MAX_WALLETS) parts.push(list.slice(i, i + MAX_WALLETS))
      const settled = await Promise.allSettled(parts.map(fetchChunk))
      const ok = settled.filter((s): s is PromiseFulfilledResult<Record<string, WalletProfile>> => s.status === 'fulfilled')
      if (!ok.length) throw new Error('profiles_unavailable')
      return Object.assign({}, ...ok.map((s) => s.value)) as Record<string, WalletProfile>
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false, dedupingInterval: 60_000, refreshInterval: 300_000, errorRetryCount: 2, errorRetryInterval: 15_000, keepPreviousData: true },
  )
  return data ?? EMPTY
}
